import { useState, useEffect, useRef } from 'react';
import { useParams, useNavigate, useSearchParams, useBlocker } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { eventApi, babyApi, photoApi } from '../services/api';
import { getBabyKey, encryptEventData, encryptData, decryptEventData, decryptData, decryptBabyProfile, encryptFile, createDecryptedImageUrl, EventData } from '../crypto';
import { useOfflineSync } from '../hooks/useOfflineSync';
import { EventType, Photo } from '../types';
import { useTimers, getSideDurations, calculateElapsedMinutes } from '../stores/useTimers';
import DurationInput from '../components/DurationInput';
import SecondsInput from '../components/SecondsInput';

const EVENT_TYPES: { type: EventType; icon: string; label: string }[] = [
  { type: EventType.FEED, icon: '🍼', label: 'Feed' },
  { type: EventType.NAPPY, icon: '🧷', label: 'Nappy' },
  { type: EventType.SLEEP, icon: '😴', label: 'Sleep' },
  { type: EventType.EC, icon: '🚽', label: 'EC Time' },
  { type: EventType.SPIT_UP, icon: '💧', label: 'Spit Up' },
  { type: EventType.MEDICATION, icon: '💊', label: 'Medication' },
  { type: EventType.WEIGHT, icon: '⚖️', label: 'Weight' },
  { type: EventType.TEMPERATURE, icon: '🌡️', label: 'Temperature' },
  { type: EventType.TUMMY_TIME, icon: '🏋️', label: 'Tummy Time' },
  { type: EventType.PUMP, icon: '🥛', label: 'Pump' },
  { type: EventType.BOTTLE, icon: '🍶', label: 'Bottle' },
  { type: EventType.JOURNAL, icon: '📝', label: 'Journal' },
  { type: EventType.SYMPTOM, icon: '🤢', label: 'Symptom' },
  { type: EventType.APPOINTMENT, icon: '🏥', label: 'Appointment' },
  { type: EventType.BLOOD_PRESSURE, icon: '🩺', label: 'BP' },
  { type: EventType.ULTRASOUND, icon: '📷', label: 'Ultrasound' },
  { type: EventType.KICK_COUNT, icon: '🦶', label: 'Kick Count' },
  { type: EventType.CONTRACTION, icon: '⏱️', label: 'Contraction' },
];

export default function AddEvent() {
  const { babyId, eventId } = useParams();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [searchParams] = useSearchParams();
  const { getTimerById, stopTimer, startUprightTimer, getActiveTimer } = useTimers();
  const { isOnline, queueEvent } = useOfflineSync();

  const isEditing = eventId !== 'add' && Boolean(eventId);

  // Get initial type from URL if provided (for new events)
  const initialType = searchParams.get('type') as EventType | null;

  // Check if coming from a timer
  const fromTimerId = searchParams.get('fromTimer');

  // Form state
  const [selectedType, setSelectedType] = useState<EventType | null>(initialType);
  const [timestamp, setTimestamp] = useState(() => {
    const now = new Date();
    now.setMinutes(now.getMinutes() - now.getTimezoneOffset());
    return now.toISOString().slice(0, 16);
  });
  const [notes, setNotes] = useState('');

  // Type-specific fields
  // Feed
  const [feedType, setFeedType] = useState<'breast' | 'formula' | 'both'>('breast');
  const [breastMode, setBreastMode] = useState<'direct' | 'measured'>('direct');
  const [feedSide, setFeedSide] = useState<'left' | 'right' | 'both'>('left');
  const [feedDuration, setFeedDuration] = useState('');
  const [feedAmount, setFeedAmount] = useState('');

  // Nappy
  const [nappyWet, setNappyWet] = useState(false);
  const [nappyDirty, setNappyDirty] = useState(false);
  const [nappyRash, setNappyRash] = useState(false);
  const [nappyBlowout, setNappyBlowout] = useState(false);

  // Sleep
  const [sleepType, setSleepType] = useState<'nap' | 'night'>('nap');
  const [sleepDuration, setSleepDuration] = useState('');

  // Medication
  const [medName, setMedName] = useState('');
  const [medDose, setMedDose] = useState('');
  const [showCustomMedInput, setShowCustomMedInput] = useState(false);

  // Weight
  const [weight, setWeight] = useState('');        // used for kg, or computed from lbs+oz
  const [weightLbs, setWeightLbs] = useState('');   // pounds (whole number, only when unit=lbs)
  const [weightOz, setWeightOz] = useState('');     // ounces (decimal 0-15.9, only when unit=lbs)
  const [weightUnit, setWeightUnit] = useState<'kg' | 'lbs'>('kg');
  const [height, setHeight] = useState('');

  const handleWeightUnitChange = (newUnit: 'kg' | 'lbs') => {
    if (newUnit !== weightUnit) {
      setWeight('');
      setWeightLbs('');
      setWeightOz('');
    }
    setWeightUnit(newUnit);
  };

  // Temperature
  const [temperature, setTemperature] = useState('');
  const [tempUnit, setTempUnit] = useState<'C' | 'F'>('C');
  const [tempMethod, setTempMethod] = useState<'oral' | 'rectal' | 'forehead' | 'armpit' | 'ear'>('forehead');

  // Tummy time (durations stored in seconds)
  const [tummyDuration, setTummyDuration] = useState('');
  const [tummyHeadUpDuration, setTummyHeadUpDuration] = useState('');
  const [tummyMood, setTummyMood] = useState<'happy' | 'neutral' | 'fussy'>('happy');

  // EC (Elimination Communication)
  const [ecPee, setEcPee] = useState<'none' | 'little' | 'loads'>('none');
  const [ecPoop, setEcPoop] = useState<'none' | 'little' | 'loads'>('none');

  // Pump
  const [pumpLeft, setPumpLeft] = useState('');
  const [pumpRight, setPumpRight] = useState('');

  // Bottle
  const [bottleType, setBottleType] = useState<'formula' | 'expressed'>('expressed');
  const [bottleAmount, setBottleAmount] = useState('');

  // Spit up
  const [spitSeverity, setSpitSeverity] = useState<'little' | 'some' | 'lots'>('little');

  // Journal
  const [journalTitle, setJournalTitle] = useState('');
  const [isMilestone, setIsMilestone] = useState(false);

  // Pregnancy - Symptom
  const [symptomType, setSymptomType] = useState('');
  const [symptomSeverity, setSymptomSeverity] = useState<'mild' | 'moderate' | 'severe'>('mild');

  // Pregnancy - Appointment
  const [appointmentType, setAppointmentType] = useState('');
  const [providerName, setProviderName] = useState('');
  const [appointmentLocation, setAppointmentLocation] = useState('');
  const [nextAppointmentDate, setNextAppointmentDate] = useState('');

  // Pregnancy - Blood Pressure
  const [systolic, setSystolic] = useState('');
  const [diastolic, setDiastolic] = useState('');
  const [heartRate, setHeartRate] = useState('');

  // Pregnancy - Ultrasound
  const [ultrasoundType, setUltrasoundType] = useState('');
  const [gestationalAgeWeeks, setGestationalAgeWeeks] = useState('');
  const [fetalHeartRate, setFetalHeartRate] = useState('');
  const [ultrasoundFindings, setUltrasoundFindings] = useState('');

  // Pregnancy - Kick Count
  const [kickCount, setKickCount] = useState('');

  // Pregnancy - Contraction
  const [contractionDuration, setContractionDuration] = useState('');
  const [contractionIntervalMinutes, setContractionIntervalMinutes] = useState('');
  const [contractionIntensity, setContractionIntensity] = useState<'mild' | 'moderate' | 'strong'>('moderate');

  // UI state
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);
  const [isLoadingEvent, setIsLoadingEvent] = useState(isEditing);
  const [timerLoaded, setTimerLoaded] = useState(false);
  const [showUprightPrompt, setShowUprightPrompt] = useState(false);
  const [uprightDuration, setUprightDuration] = useState(0);

  // Track unsaved form changes for navigation guard
  const formDirtyRef = useRef(Boolean(fromTimerId));
  const initialTimestampRef = useRef(timestamp);
  const initialNotesRef = useRef(notes);
  const [showExitConfirm, setShowExitConfirm] = useState(false);

  // Mark form dirty when user selects an event type (new events)
  useEffect(() => {
    if (selectedType && !formDirtyRef.current) {
      formDirtyRef.current = true;
    }
  }, [selectedType]);

  // Mark form dirty when timestamp or notes change (edit mode change detection)
  useEffect(() => {
    if (timestamp !== initialTimestampRef.current || notes !== initialNotesRef.current) {
      formDirtyRef.current = true;
    }
  }, [timestamp, notes]);

  // Browser-level navigation guard (tab close, refresh)
  useEffect(() => {
    const handler = (e: BeforeUnloadEvent) => {
      if (formDirtyRef.current) {
        e.preventDefault();
        e.returnValue = '';
      }
    };
    window.addEventListener('beforeunload', handler);
    return () => window.removeEventListener('beforeunload', handler);
  }, []);

  // In-app navigation blocker
  const blocker = useBlocker(
    ({ currentLocation, nextLocation }) =>
      formDirtyRef.current &&
      currentLocation.pathname !== nextLocation.pathname
  );

  // When blocker activates, show our confirmation modal
  useEffect(() => {
    if (blocker.state === 'blocked') {
      setShowExitConfirm(true);
    }
  }, [blocker.state]);

  // Photo state
  const [pendingPhotos, setPendingPhotos] = useState<Array<{
    file: File;
    preview: string;
    photoId: string | null;
    uploading: boolean;
    error: string | null;
  }>>([]);
  const [existingPhotos, setExistingPhotos] = useState<Photo[]>([]);
  const [removedPhotoIds, setRemovedPhotoIds] = useState<string[]>([]);
  const [existingPhotoUrls, setExistingPhotoUrls] = useState<Record<string, string>>({});

  // Fetch baby profile to get postFeedUprightMinutes setting and name
  const { data: babyProfile } = useQuery({
    queryKey: ['baby-profile-settings', babyId],
    queryFn: async () => {
      if (!babyId) return null;
      const response = await babyApi.getById(babyId);
      if (!response.data) return null;
      const aesKey = await getBabyKey(babyId);
      if (!aesKey) return null;
      const profile = await decryptBabyProfile(response.data.encryptedData, aesKey);
      return {
        name: profile.name,
        postFeedUprightMinutes: profile.postFeedUprightMinutes ?? null,
        medications: profile.medications ?? [],
      };
    },
    enabled: !!babyId,
    staleTime: 5 * 60 * 1000,
  });
  const postFeedUprightMinutes = babyProfile?.postFeedUprightMinutes ?? null;
  const babyName = babyProfile?.name;
  const profileMedications = babyProfile?.medications ?? [];

  // Load data from timer if coming from a timer
  useEffect(() => {
    if (!fromTimerId || timerLoaded) return;

    const timer = getTimerById(fromTimerId);
    if (!timer) {
      setTimerLoaded(true);
      return;
    }

    // Set the timestamp to when the timer started
    const startDate = new Date(timer.startTime);
    startDate.setMinutes(startDate.getMinutes() - startDate.getTimezoneOffset());
    setTimestamp(startDate.toISOString().slice(0, 16));

    // Calculate duration accounting for paused time
    const durationMinutes = calculateElapsedMinutes(
      timer.startTime,
      timer.totalPausedTime,
      timer.isPaused,
      timer.pausedAt
    );

    if (timer.type === 'FEED') {
      setSelectedType(EventType.FEED);
      setFeedType('breast');
      setFeedDuration(String(durationMinutes));

      // Set the last side used
      if (timer.side) {
        setFeedSide(timer.side);
      }

      // If we have side durations, we could add them to notes
      const sideDurations = getSideDurations(timer);
      if (sideDurations.left > 0 || sideDurations.right > 0) {
        const sideInfo = [];
        if (sideDurations.left > 0) sideInfo.push(`Left: ${sideDurations.left}m`);
        if (sideDurations.right > 0) sideInfo.push(`Right: ${sideDurations.right}m`);
        setNotes(sideInfo.join(', '));
      }
    } else if (timer.type === 'SLEEP') {
      setSelectedType(EventType.SLEEP);
      setSleepDuration(String(durationMinutes));
      if (timer.sleepType) {
        setSleepType(timer.sleepType);
      }
    } else if (timer.type === 'TUMMY') {
      setSelectedType(EventType.TUMMY_TIME);
      // Duration in seconds from URL params (passed by TimerWidget)
      const tummySecsParam = searchParams.get('tummySeconds');
      const headUpSecsParam = searchParams.get('headUpSeconds');
      if (tummySecsParam) setTummyDuration(tummySecsParam);
      if (headUpSecsParam) setTummyHeadUpDuration(headUpSecsParam);
    }

    // Stop and remove the timer
    stopTimer(fromTimerId);
    setTimerLoaded(true);
  }, [fromTimerId, getTimerById, stopTimer, timerLoaded]);

  // Fetch existing event when editing
  const { data: existingEvent } = useQuery({
    queryKey: ['event', eventId],
    queryFn: async () => {
      if (!eventId) return null;
      const response = await eventApi.getById(eventId);
      return response.data;
    },
    enabled: isEditing && Boolean(eventId),
  });

  // Load and decrypt existing event data
  useEffect(() => {
    const loadEventData = async () => {
      if (!existingEvent || !babyId) return;

      setIsLoadingEvent(true);
      try {
        const aesKey = await getBabyKey(babyId);
        if (!aesKey) {
          setError('Could not find encryption key');
          setIsLoadingEvent(false);
          return;
        }

        // Decrypt event data
        const decryptedData = await decryptEventData(existingEvent.encryptedData, aesKey);

        // Set the event type
        setSelectedType(existingEvent.type as EventType);

        // Set timestamp
        const eventDate = new Date(existingEvent.timestamp);
        eventDate.setMinutes(eventDate.getMinutes() - eventDate.getTimezoneOffset());
        setTimestamp(eventDate.toISOString().slice(0, 16));

        // Decrypt and set notes if present
        if (existingEvent.encryptedNotes) {
          try {
            const decryptedNotes = await decryptData(existingEvent.encryptedNotes, aesKey);
            setNotes(decryptedNotes);
          } catch {
            // Notes may be optional, ignore decryption errors
          }
        }

        // Populate type-specific fields based on event type
        populateFieldsFromData(existingEvent.type as EventType, decryptedData);

        // Load existing photos
        if (existingEvent.photos && existingEvent.photos.length > 0) {
          setExistingPhotos(existingEvent.photos);
          for (const photo of existingEvent.photos) {
            photoApi.downloadDirect(photo.id)
              .then(({ blob, mimeType }) => createDecryptedImageUrl(blob, aesKey, mimeType))
              .then(url => setExistingPhotoUrls(prev => ({ ...prev, [photo.id]: url })))
              .catch(() => {});
          }
        }

      } catch (err) {
        console.error('Failed to load event:', err);
        setError('Failed to load event data');
      } finally {
        setIsLoadingEvent(false);
      }
    };

    loadEventData();
  }, [existingEvent, babyId]);

  // Populate form fields from decrypted data
  const populateFieldsFromData = (type: EventType, data: EventData) => {
    switch (type) {
      case EventType.FEED:
        if (data.feedType) setFeedType(data.feedType as 'breast' | 'formula' | 'both');
        if (data.side) setFeedSide(data.side as 'left' | 'right' | 'both');
        if (data.duration) setFeedDuration(String(data.duration));
        if (data.amount) setFeedAmount(String(data.amount));
        // Detect measured breast feed: has amount but no side/duration
        if (data.feedType === 'breast' && data.amount && !data.side) {
          setBreastMode('measured');
        }
        break;

      case EventType.NAPPY:
        setNappyWet(Boolean(data.wet));
        setNappyDirty(Boolean(data.dirty));
        setNappyRash(Boolean(data.rash));
        setNappyBlowout(Boolean(data.blowout));
        break;

      case EventType.SLEEP:
        if (data.sleepType) setSleepType(data.sleepType as 'nap' | 'night');
        if (data.duration) setSleepDuration(String(data.duration));
        break;

      case EventType.MEDICATION:
        if (data.medicationName) {
          setMedName(String(data.medicationName));
          // If the medication doesn't match any profile medication, show custom input
          if (profileMedications.length > 0 && !profileMedications.includes(String(data.medicationName))) {
            setShowCustomMedInput(true);
          }
        }
        if (data.dose) setMedDose(String(data.dose));
        break;

      case EventType.WEIGHT:
        if (data.unit) setWeightUnit(data.unit as 'kg' | 'lbs');
        if (data.weight !== undefined) {
          if (data.unit === 'lbs') {
            // Convert decimal lbs → lbs + oz for the dual-input fields
            const totalLbs = data.weight;
            const wholeLbs = Math.floor(totalLbs);
            const oz = parseFloat(((totalLbs - wholeLbs) * 16).toFixed(1));
            setWeightLbs(String(wholeLbs));
            setWeightOz(String(oz));
            // Also set weight for kg conversion display
            setWeight(String(totalLbs));
          } else {
            setWeight(String(data.weight));
          }
        }
        if (data.height) setHeight(String(data.height));
        break;

      case EventType.TEMPERATURE:
        if (data.temperature) setTemperature(String(data.temperature));
        if (data.tempUnit) setTempUnit(data.tempUnit as 'C' | 'F');
        if (data.method) setTempMethod(data.method as typeof tempMethod);
        break;

      case EventType.TUMMY_TIME:
        if (data.duration) setTummyDuration(String(data.duration));
        if (data.headUpDuration) setTummyHeadUpDuration(String(data.headUpDuration));
        if (data.mood) setTummyMood(data.mood as 'happy' | 'neutral' | 'fussy');
        break;

      case EventType.EC:
        if (data.ecPee) setEcPee(data.ecPee as 'none' | 'little' | 'loads');
        if (data.ecPoop) setEcPoop(data.ecPoop as 'none' | 'little' | 'loads');
        break;

      case EventType.PUMP:
        if (data.leftAmount) setPumpLeft(String(data.leftAmount));
        if (data.rightAmount) setPumpRight(String(data.rightAmount));
        break;

      case EventType.BOTTLE:
        if (data.bottleType) setBottleType(data.bottleType as 'formula' | 'expressed');
        if (data.amountOffered) setBottleAmount(String(data.amountOffered));
        break;

      case EventType.SPIT_UP:
        if (data.severity) setSpitSeverity(data.severity as 'little' | 'some' | 'lots');
        break;

      case EventType.JOURNAL:
        if (data.title) setJournalTitle(String(data.title));
        setIsMilestone(Boolean(data.isMilestone));
        break;

      case EventType.SYMPTOM:
        if (data.symptomType) setSymptomType(String(data.symptomType));
        if (data.symptomSeverity) setSymptomSeverity(data.symptomSeverity as 'mild' | 'moderate' | 'severe');
        break;

      case EventType.APPOINTMENT:
        if (data.appointmentType) setAppointmentType(String(data.appointmentType));
        if (data.providerName) setProviderName(String(data.providerName));
        if (data.appointmentLocation) setAppointmentLocation(String(data.appointmentLocation));
        if (data.nextAppointmentDate) setNextAppointmentDate(String(data.nextAppointmentDate));
        break;

      case EventType.BLOOD_PRESSURE:
        if (data.systolic) setSystolic(String(data.systolic));
        if (data.diastolic) setDiastolic(String(data.diastolic));
        if (data.heartRate) setHeartRate(String(data.heartRate));
        break;

      case EventType.ULTRASOUND:
        if (data.ultrasoundType) setUltrasoundType(String(data.ultrasoundType));
        if (data.gestationalAgeWeeks) setGestationalAgeWeeks(String(data.gestationalAgeWeeks));
        if (data.fetalHeartRate) setFetalHeartRate(String(data.fetalHeartRate));
        if (data.ultrasoundFindings) setUltrasoundFindings(String(data.ultrasoundFindings));
        break;

      case EventType.KICK_COUNT:
        if (data.kickCount) setKickCount(String(data.kickCount));
        break;

      case EventType.CONTRACTION:
        if (data.contractionDuration) setContractionDuration(String(data.contractionDuration));
        if (data.contractionIntervalMinutes) setContractionIntervalMinutes(String(data.contractionIntervalMinutes));
        if (data.contractionIntensity) setContractionIntensity(data.contractionIntensity as 'mild' | 'moderate' | 'strong');
        break;
    }
  };

  const handlePhotoSelect = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = Array.from(e.target.files || []);
    e.target.value = '';
    if (!files.length || !babyId) return;

    const slots = 2 - (existingPhotos.length + pendingPhotos.length);
    const toUpload = files.slice(0, slots);
    if (!toUpload.length) return;

    const aesKey = await getBabyKey(babyId);
    if (!aesKey) return;

    const newEntries = toUpload.map(file => ({
      file,
      preview: URL.createObjectURL(file),
      photoId: null as string | null,
      uploading: true,
      error: null as string | null,
    }));

    setPendingPhotos(prev => [...prev, ...newEntries]);

    for (const entry of newEntries) {
      try {
        const encryptedBlob = await encryptFile(entry.file, aesKey);
        const buffer = await encryptedBlob.arrayBuffer();
        const result = await photoApi.uploadDirect(babyId, { mimeType: entry.file.type, encryptedKey: '' }, buffer);
        setPendingPhotos(prev => prev.map(p =>
          p.preview === entry.preview ? { ...p, photoId: result.data!.photoId, uploading: false } : p
        ));
      } catch {
        setPendingPhotos(prev => prev.map(p =>
          p.preview === entry.preview ? { ...p, error: 'Upload failed', uploading: false } : p
        ));
      }
    }
  };

  const handleRemovePending = (index: number) => {
    const photo = pendingPhotos[index];
    URL.revokeObjectURL(photo.preview);
    if (photo.photoId) {
      photoApi.delete(photo.photoId).catch(() => {});
    }
    setPendingPhotos(prev => prev.filter((_, i) => i !== index));
  };

  const handleRemoveExisting = (photoId: string) => {
    setExistingPhotos(prev => prev.filter(p => p.id !== photoId));
    setRemovedPhotoIds(prev => [...prev, photoId]);
    setExistingPhotoUrls(prev => {
      const next = { ...prev };
      delete next[photoId];
      return next;
    });
  };

  // Create mutation
  const createMutation = useMutation({
    mutationFn: async (data: { type: string; timestamp: string; encryptedData: string; encryptedNotes?: string; photoIds?: string[] }) => {
      const response = await eventApi.create(babyId!, data);
      return response.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['events', babyId] });
      queryClient.invalidateQueries({ queryKey: ['latestEvents', babyId] });
      queryClient.invalidateQueries({ queryKey: ['todayEvents', babyId] });
    },
  });

  // Update mutation
  const updateMutation = useMutation({
    mutationFn: async (data: { timestamp: string; encryptedData: string; encryptedNotes?: string; photoIds?: string[] }) => {
      const response = await eventApi.update(eventId!, data);
      return response.data;
    },
    onSuccess: () => {
      formDirtyRef.current = false;
      queryClient.invalidateQueries({ queryKey: ['events', babyId] });
      queryClient.invalidateQueries({ queryKey: ['latestEvents', babyId] });
      queryClient.invalidateQueries({ queryKey: ['todayEvents', babyId] });
      queryClient.invalidateQueries({ queryKey: ['event', eventId] });
      navigate(`/app/baby/${babyId}/history`);
    },
  });

  // Delete mutation
  const deleteMutation = useMutation({
    mutationFn: async () => {
      await eventApi.delete(eventId!);
    },
    onSuccess: () => {
      formDirtyRef.current = false;
      queryClient.invalidateQueries({ queryKey: ['events', babyId] });
      queryClient.invalidateQueries({ queryKey: ['latestEvents', babyId] });
      navigate(`/app/baby/${babyId}/history`);
    },
  });

  const buildEventData = () => {
    const data: Record<string, unknown> = {};

    switch (selectedType) {
      case EventType.FEED:
        data.feedType = feedType;
        if (feedType === 'breast') {
          if (breastMode === 'direct') {
            data.side = feedSide;
            if (feedDuration) data.duration = parseInt(feedDuration);
          } else {
            // measured breast feed — store amount only
            if (feedAmount) data.amount = parseInt(feedAmount);
          }
        } else {
          if (feedAmount) data.amount = parseInt(feedAmount);
        }
        break;

      case EventType.NAPPY:
        data.wet = nappyWet;
        data.dirty = nappyDirty;
        data.rash = nappyRash;
        data.blowout = nappyBlowout;
        break;

      case EventType.SLEEP:
        data.sleepType = sleepType;
        if (sleepDuration) data.duration = parseInt(sleepDuration);
        break;

      case EventType.MEDICATION:
        data.medicationName = medName;
        if (medDose) data.dose = medDose;
        break;

      case EventType.WEIGHT:
        if (weightUnit === 'lbs') {
          const lbs = parseInt(weightLbs) || 0;
          const oz = parseFloat(weightOz) || 0;
          if (weightLbs || weightOz) {
            data.weight = parseFloat((lbs + oz / 16).toFixed(3));
          }
        } else {
          if (weight) data.weight = parseFloat(weight);
        }
        data.unit = weightUnit;
        if (height) data.height = parseFloat(height);
        break;

      case EventType.TEMPERATURE:
        if (temperature) data.temperature = parseFloat(temperature);
        data.tempUnit = tempUnit;
        data.method = tempMethod;
        break;

      case EventType.TUMMY_TIME:
        if (tummyDuration) data.duration = parseInt(tummyDuration);
        if (tummyHeadUpDuration) data.headUpDuration = parseInt(tummyHeadUpDuration);
        data.mood = tummyMood;
        break;

      case EventType.EC:
        data.ecPee = ecPee;
        data.ecPoop = ecPoop;
        break;

      case EventType.PUMP:
        if (pumpLeft) data.leftAmount = parseInt(pumpLeft);
        if (pumpRight) data.rightAmount = parseInt(pumpRight);
        break;

      case EventType.BOTTLE:
        data.bottleType = bottleType;
        if (bottleAmount) data.amountOffered = parseInt(bottleAmount);
        break;

      case EventType.SPIT_UP:
        data.severity = spitSeverity;
        break;

      case EventType.JOURNAL:
        if (journalTitle) data.title = journalTitle;
        data.isMilestone = isMilestone;
        break;

      case EventType.SYMPTOM:
        data.symptomType = symptomType;
        data.symptomSeverity = symptomSeverity;
        break;

      case EventType.APPOINTMENT:
        data.appointmentType = appointmentType;
        if (providerName) data.providerName = providerName;
        if (appointmentLocation) data.appointmentLocation = appointmentLocation;
        if (nextAppointmentDate) data.nextAppointmentDate = nextAppointmentDate;
        break;

      case EventType.BLOOD_PRESSURE:
        if (systolic) data.systolic = parseInt(systolic);
        if (diastolic) data.diastolic = parseInt(diastolic);
        if (heartRate) data.heartRate = parseInt(heartRate);
        break;

      case EventType.ULTRASOUND:
        data.ultrasoundType = ultrasoundType;
        if (gestationalAgeWeeks) data.gestationalAgeWeeks = parseFloat(gestationalAgeWeeks);
        if (fetalHeartRate) data.fetalHeartRate = parseInt(fetalHeartRate);
        if (ultrasoundFindings) data.ultrasoundFindings = ultrasoundFindings;
        break;

      case EventType.KICK_COUNT:
        if (kickCount) data.kickCount = parseInt(kickCount);
        break;

      case EventType.CONTRACTION:
        if (contractionDuration) data.contractionDuration = parseInt(contractionDuration);
        if (contractionIntervalMinutes) data.contractionIntervalMinutes = parseInt(contractionIntervalMinutes);
        data.contractionIntensity = contractionIntensity;
        break;
    }

    return data;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);

    if (!selectedType) {
      setError('Please select an event type');
      return;
    }

    if (!babyId) {
      setError('No baby selected');
      return;
    }

    if (pendingPhotos.some(p => p.uploading)) {
      setError('Please wait for photos to finish uploading');
      return;
    }

    setIsSubmitting(true);

    // Collect photo IDs
    const photoIds = [
      ...existingPhotos.map(p => p.id),
      ...pendingPhotos.filter(p => p.photoId).map(p => p.photoId!),
    ];

    // Fire-and-forget: delete removed existing photos
    if (removedPhotoIds.length > 0) {
      removedPhotoIds.forEach(id => photoApi.delete(id).catch(() => {}));
    }

    try {
      const aesKey = await getBabyKey(babyId);
      if (!aesKey) {
        setError('Could not find encryption key');
        setIsSubmitting(false);
        return;
      }

      const eventData = buildEventData();
      const encryptedData = await encryptEventData(eventData, aesKey);

      let encryptedNotes: string | undefined;
      if (notes.trim()) {
        encryptedNotes = await encryptData(notes.trim(), aesKey);
      }

      // Offline handling: queue the event if no network
      if (!isOnline) {
        if (isEditing) {
          setError('Edits require a network connection');
          setIsSubmitting(false);
          return;
        }
        await queueEvent({
          babyId: babyId!,
          type: selectedType,
          timestamp: new Date(timestamp).toISOString(),
          encryptedData,
          encryptedNotes,
        });
        formDirtyRef.current = false;
        navigate(`/app/baby/${babyId}/history`);
        return;
      }

      if (isEditing) {
        // Update existing event
        await updateMutation.mutateAsync({
          timestamp: new Date(timestamp).toISOString(),
          encryptedData,
          encryptedNotes,
          photoIds: photoIds.length > 0 ? photoIds : undefined,
        });
      } else {
        // Create new event
        await createMutation.mutateAsync({
          type: selectedType,
          timestamp: new Date(timestamp).toISOString(),
          encryptedData,
          encryptedNotes,
          photoIds: photoIds.length > 0 ? photoIds : undefined,
        });

        // Check if we should offer the upright timer prompt
        const isFeedEvent = selectedType === EventType.FEED || selectedType === EventType.BOTTLE;
        if (isFeedEvent && postFeedUprightMinutes && postFeedUprightMinutes > 0) {
          // Check if there's already an active upright timer
          const existingUpright = getActiveTimer(babyId!, 'UPRIGHT');
          if (!existingUpright) {
            formDirtyRef.current = false;
            setUprightDuration(postFeedUprightMinutes);
            setShowUprightPrompt(true);
            setIsSubmitting(false);
            return;
          }
        }

        formDirtyRef.current = false;
        navigate(`/app/baby/${babyId}/history`);
      }
    } catch (err) {
      console.error('Failed to save event:', err);
      setError((err as Error).message || 'Failed to save event');
      setIsSubmitting(false);
    }
  };

  const handleDelete = async () => {
    setIsDeleting(true);
    try {
      await deleteMutation.mutateAsync();
    } catch (err) {
      console.error('Failed to delete event:', err);
      setError((err as Error).message || 'Failed to delete event');
      setIsDeleting(false);
      setShowDeleteConfirm(false);
    }
  };

  const renderTypeSpecificFields = () => {
    switch (selectedType) {
      case EventType.FEED:
        return (
          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                Feed Type
              </label>
              <div className="flex gap-2">
                {(['breast', 'formula', 'both'] as const).map((type) => (
                  <button
                    key={type}
                    type="button"
                    onClick={() => setFeedType(type)}
                    className={`px-4 py-2 rounded-lg capitalize ${
                      feedType === type
                        ? 'bg-primary-600 text-white'
                        : 'bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300'
                    }`}
                  >
                    {type}
                  </button>
                ))}
              </div>
            </div>

            {feedType === 'breast' && (
              <>
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                    Tracking Method
                  </label>
                  <div className="flex gap-2">
                    <button
                      type="button"
                      onClick={() => setBreastMode('direct')}
                      className={`px-4 py-2 rounded-lg ${
                        breastMode === 'direct'
                          ? 'bg-primary-600 text-white'
                          : 'bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300'
                      }`}
                    >
                      Direct Feed
                    </button>
                    <button
                      type="button"
                      onClick={() => setBreastMode('measured')}
                      className={`px-4 py-2 rounded-lg ${
                        breastMode === 'measured'
                          ? 'bg-primary-600 text-white'
                          : 'bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300'
                      }`}
                    >
                      Expressed/Measured
                    </button>
                  </div>
                </div>

                {breastMode === 'direct' ? (
                  <>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                        Side
                      </label>
                      <div className="flex gap-2">
                        {(['left', 'right', 'both'] as const).map((side) => (
                          <button
                            key={side}
                            type="button"
                            onClick={() => setFeedSide(side)}
                            className={`px-4 py-2 rounded-lg capitalize ${
                              feedSide === side
                                ? 'bg-primary-600 text-white'
                                : 'bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300'
                            }`}
                          >
                            {side}
                          </button>
                        ))}
                      </div>
                    </div>
                    <DurationInput
                      value={feedDuration}
                      onChange={setFeedDuration}
                      label="Duration"
                      placeholder="0:15"
                    />
                  </>
                ) : (
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                      Amount (ml)
                    </label>
                    <input
                      type="number"
                      value={feedAmount}
                      onChange={(e) => setFeedAmount(e.target.value)}
                      className="input"
                      placeholder="120"
                      min="0"
                    />
                  </div>
                )}
              </>
            )}

            {feedType !== 'breast' && (
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  Amount (ml)
                </label>
                <input
                  type="number"
                  value={feedAmount}
                  onChange={(e) => setFeedAmount(e.target.value)}
                  className="input"
                  placeholder="120"
                  min="0"
                />
              </div>
            )}
          </div>
        );

      case EventType.NAPPY:
        return (
          <div className="space-y-4">
            <div className="flex flex-col gap-3">
              <label className="flex items-center gap-3 p-3 bg-gray-50 dark:bg-gray-800 rounded-lg cursor-pointer">
                <input
                  type="checkbox"
                  checked={nappyWet}
                  onChange={(e) => setNappyWet(e.target.checked)}
                  className="w-5 h-5 text-primary-600"
                />
                <span className="text-gray-900 dark:text-white">Wet 💦</span>
              </label>
              <label className="flex items-center gap-3 p-3 bg-gray-50 dark:bg-gray-800 rounded-lg cursor-pointer">
                <input
                  type="checkbox"
                  checked={nappyDirty}
                  onChange={(e) => setNappyDirty(e.target.checked)}
                  className="w-5 h-5 text-primary-600"
                />
                <span className="text-gray-900 dark:text-white">Dirty 💩</span>
              </label>
              <label className="flex items-center gap-3 p-3 bg-gray-50 dark:bg-gray-800 rounded-lg cursor-pointer">
                <input
                  type="checkbox"
                  checked={nappyRash}
                  onChange={(e) => setNappyRash(e.target.checked)}
                  className="w-5 h-5 text-primary-600"
                />
                <span className="text-gray-900 dark:text-white">Rash noted</span>
              </label>
              <label className="flex items-center gap-3 p-3 bg-red-50 dark:bg-red-900/20 rounded-lg cursor-pointer border border-red-200 dark:border-red-800">
                <input
                  type="checkbox"
                  checked={nappyBlowout}
                  onChange={(e) => setNappyBlowout(e.target.checked)}
                  className="w-5 h-5 text-red-600"
                />
                <span className="text-gray-900 dark:text-white">💥 Blowout (Poonarmi)</span>
              </label>
            </div>
          </div>
        );

      case EventType.SLEEP:
        return (
          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                Sleep Type
              </label>
              <div className="flex gap-2">
                {(['nap', 'night'] as const).map((type) => (
                  <button
                    key={type}
                    type="button"
                    onClick={() => setSleepType(type)}
                    className={`px-4 py-2 rounded-lg capitalize ${
                      sleepType === type
                        ? 'bg-primary-600 text-white'
                        : 'bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300'
                    }`}
                  >
                    {type === 'nap' ? '😴 Nap' : '🌙 Night Sleep'}
                  </button>
                ))}
              </div>
            </div>
            <DurationInput
              value={sleepDuration}
              onChange={setSleepDuration}
              label="Duration"
              placeholder="0:45"
            />
          </div>
        );

      case EventType.MEDICATION:
        return (
          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                Medication Name *
              </label>
              {profileMedications.length > 0 ? (
                <>
                  <div className="flex flex-wrap gap-2 mb-2">
                    {profileMedications.map((med) => (
                      <button
                        key={med}
                        type="button"
                        onClick={() => {
                          setMedName(med);
                          setShowCustomMedInput(false);
                        }}
                        className={`px-4 py-2 rounded-lg ${
                          medName === med && !showCustomMedInput
                            ? 'bg-primary-600 text-white'
                            : 'bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300'
                        }`}
                      >
                        {med}
                      </button>
                    ))}
                    <button
                      type="button"
                      onClick={() => {
                        setShowCustomMedInput(true);
                        if (profileMedications.includes(medName)) {
                          setMedName('');
                        }
                      }}
                      className={`px-4 py-2 rounded-lg ${
                        showCustomMedInput
                          ? 'bg-primary-600 text-white'
                          : 'bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300'
                      }`}
                    >
                      Other...
                    </button>
                  </div>
                  {showCustomMedInput && (
                    <input
                      type="text"
                      value={medName}
                      onChange={(e) => setMedName(e.target.value)}
                      className="input"
                      placeholder="Enter medication name"
                      required
                      autoFocus
                    />
                  )}
                </>
              ) : (
                <input
                  type="text"
                  value={medName}
                  onChange={(e) => setMedName(e.target.value)}
                  className="input"
                  placeholder="Paracetamol"
                  required
                />
              )}
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                Dose
              </label>
              <input
                type="text"
                value={medDose}
                onChange={(e) => setMedDose(e.target.value)}
                className="input"
                placeholder="2.5ml"
              />
            </div>
          </div>
        );

      case EventType.WEIGHT:
        return (
          <div className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  Unit
                </label>
                <select
                  value={weightUnit}
                  onChange={(e) => handleWeightUnitChange(e.target.value as 'kg' | 'lbs')}
                  className="input"
                >
                  <option value="kg">kg</option>
                  <option value="lbs">lbs + oz</option>
                </select>
              </div>
            </div>

            {weightUnit === 'kg' ? (
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  Weight (kg)
                </label>
                <input
                  type="number"
                  step="0.01"
                  value={weight}
                  onChange={(e) => setWeight(e.target.value)}
                  className="input"
                  placeholder="4.5"
                />
              </div>
            ) : (
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  Weight
                </label>
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <label className="block text-xs text-gray-500 dark:text-gray-400 mb-1">
                      Pounds (lbs)
                    </label>
                    <input
                      type="number"
                      step="1"
                      min="0"
                      value={weightLbs}
                      onChange={(e) => setWeightLbs(e.target.value)}
                      className="input"
                      placeholder="17"
                    />
                  </div>
                  <div>
                    <label className="block text-xs text-gray-500 dark:text-gray-400 mb-1">
                      Ounces (oz)
                    </label>
                    <input
                      type="number"
                      step="0.1"
                      min="0"
                      max="15.9"
                      value={weightOz}
                      onChange={(e) => setWeightOz(e.target.value)}
                      className="input"
                      placeholder="10"
                    />
                  </div>
                </div>
                {weightLbs && (
                  <p className="mt-2 text-sm text-gray-500 dark:text-gray-400">
                    = {(parseInt(weightLbs) || 0) + (parseFloat(weightOz) || 0) / 16} lbs
                    {' ≈ '}
                    {(((parseInt(weightLbs) || 0) + (parseFloat(weightOz) || 0) / 16) * 0.453592).toFixed(2)} kg
                  </p>
                )}
              </div>
            )}

            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                Height (cm)
              </label>
              <input
                type="number"
                step="0.1"
                value={height}
                onChange={(e) => setHeight(e.target.value)}
                className="input"
                placeholder="55"
              />
            </div>
          </div>
        );

      case EventType.TEMPERATURE:
        return (
          <div className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  Temperature
                </label>
                <input
                  type="number"
                  step="0.1"
                  value={temperature}
                  onChange={(e) => setTemperature(e.target.value)}
                  className="input"
                  placeholder="36.5"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  Unit
                </label>
                <select
                  value={tempUnit}
                  onChange={(e) => setTempUnit(e.target.value as 'C' | 'F')}
                  className="input"
                >
                  <option value="C">°C</option>
                  <option value="F">°F</option>
                </select>
              </div>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                Method
              </label>
              <select
                value={tempMethod}
                onChange={(e) => setTempMethod(e.target.value as typeof tempMethod)}
                className="input"
              >
                <option value="forehead">Forehead</option>
                <option value="ear">Ear</option>
                <option value="armpit">Armpit</option>
                <option value="oral">Oral</option>
                <option value="rectal">Rectal</option>
              </select>
            </div>
          </div>
        );

      case EventType.TUMMY_TIME:
        return (
          <div className="space-y-4">
            <SecondsInput
              value={tummyDuration}
              onChange={setTummyDuration}
              label="Total Tummy Time"
              placeholder="0:00"
            />
            <SecondsInput
              value={tummyHeadUpDuration}
              onChange={setTummyHeadUpDuration}
              label="Head Up Time (within session)"
              placeholder="0:00"
            />
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                Mood
              </label>
              <div className="flex gap-2">
                {([
                  { value: 'happy', label: '😊 Happy' },
                  { value: 'neutral', label: '😐 Neutral' },
                  { value: 'fussy', label: '😢 Fussy' },
                ] as const).map(({ value, label }) => (
                  <button
                    key={value}
                    type="button"
                    onClick={() => setTummyMood(value)}
                    className={`px-4 py-2 rounded-lg ${
                      tummyMood === value
                        ? 'bg-primary-600 text-white'
                        : 'bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300'
                    }`}
                  >
                    {label}
                  </button>
                ))}
              </div>
            </div>
          </div>
        );

      case EventType.PUMP:
        return (
          <div className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  Left (ml)
                </label>
                <input
                  type="number"
                  value={pumpLeft}
                  onChange={(e) => setPumpLeft(e.target.value)}
                  className="input"
                  placeholder="60"
                  min="0"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  Right (ml)
                </label>
                <input
                  type="number"
                  value={pumpRight}
                  onChange={(e) => setPumpRight(e.target.value)}
                  className="input"
                  placeholder="60"
                  min="0"
                />
              </div>
            </div>
            {(pumpLeft || pumpRight) && (
              <p className="text-sm text-gray-600 dark:text-gray-400">
                Total: {(parseInt(pumpLeft) || 0) + (parseInt(pumpRight) || 0)} ml
              </p>
            )}
          </div>
        );

      case EventType.BOTTLE:
        return (
          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                Bottle Type
              </label>
              <div className="flex gap-2">
                {([
                  { value: 'expressed', label: '🤱 Expressed Milk' },
                  { value: 'formula', label: '🍼 Formula' },
                ] as const).map(({ value, label }) => (
                  <button
                    key={value}
                    type="button"
                    onClick={() => setBottleType(value)}
                    className={`px-4 py-2 rounded-lg ${
                      bottleType === value
                        ? 'bg-primary-600 text-white'
                        : 'bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300'
                    }`}
                  >
                    {label}
                  </button>
                ))}
              </div>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                Amount (ml)
              </label>
              <input
                type="number"
                value={bottleAmount}
                onChange={(e) => setBottleAmount(e.target.value)}
                className="input"
                placeholder="120"
                min="0"
              />
            </div>
          </div>
        );

      case EventType.SPIT_UP:
        return (
          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                Severity
              </label>
              <div className="flex gap-2">
                {([
                  { value: 'little', label: 'A little' },
                  { value: 'some', label: 'Some' },
                  { value: 'lots', label: 'A lot' },
                ] as const).map(({ value, label }) => (
                  <button
                    key={value}
                    type="button"
                    onClick={() => setSpitSeverity(value)}
                    className={`px-4 py-2 rounded-lg ${
                      spitSeverity === value
                        ? 'bg-primary-600 text-white'
                        : 'bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300'
                    }`}
                  >
                    {label}
                  </button>
                ))}
              </div>
            </div>
          </div>
        );

      case EventType.JOURNAL:
        return (
          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                Title
              </label>
              <input
                type="text"
                value={journalTitle}
                onChange={(e) => setJournalTitle(e.target.value)}
                className="input"
                placeholder="First smile!"
              />
            </div>
            <label className="flex items-center gap-3 p-3 bg-yellow-50 dark:bg-yellow-900/20 rounded-lg cursor-pointer">
              <input
                type="checkbox"
                checked={isMilestone}
                onChange={(e) => setIsMilestone(e.target.checked)}
                className="w-5 h-5 text-primary-600"
              />
              <span className="text-gray-900 dark:text-white">⭐ Mark as milestone</span>
            </label>
          </div>
        );

      case EventType.EC:
        return (
          <div className="space-y-6">
            <p className="text-sm text-gray-600 dark:text-gray-400 bg-blue-50 dark:bg-blue-900/20 p-3 rounded-lg">
              EC (Elimination Communication) — hold baby in position and record what happened. "None" is also valuable data!
            </p>
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                Pee 💧
              </label>
              <div className="flex gap-2">
                {([
                  { value: 'none', label: 'None' },
                  { value: 'little', label: 'A little' },
                  { value: 'loads', label: 'Loads' },
                ] as const).map(({ value, label }) => (
                  <button
                    key={value}
                    type="button"
                    onClick={() => setEcPee(value)}
                    className={`px-4 py-2 rounded-lg ${
                      ecPee === value
                        ? 'bg-blue-500 text-white'
                        : 'bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300'
                    }`}
                  >
                    {label}
                  </button>
                ))}
              </div>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                Poop 💩
              </label>
              <div className="flex gap-2">
                {([
                  { value: 'none', label: 'None' },
                  { value: 'little', label: 'A little' },
                  { value: 'loads', label: 'Loads' },
                ] as const).map(({ value, label }) => (
                  <button
                    key={value}
                    type="button"
                    onClick={() => setEcPoop(value)}
                    className={`px-4 py-2 rounded-lg ${
                      ecPoop === value
                        ? 'bg-amber-600 text-white'
                        : 'bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300'
                    }`}
                  >
                    {label}
                  </button>
                ))}
              </div>
            </div>
          </div>
        );

      case EventType.SYMPTOM:
        return (
          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                Symptom Type
              </label>
              <div className="flex flex-wrap gap-2">
                {['nausea', 'vomiting', 'fatigue', 'exhaustion', 'headache', 'dizziness', 'heartburn', 'bloating', 'constipation', 'diarrhoea', 'cramps', 'back_pain', 'pelvic_pain', 'sore_breasts', 'swelling', 'itching', 'stuffy_nose', 'frequent_urination', 'food_aversion', 'food_cravings', 'insomnia', 'spotting', 'vaginal_discharge', 'contractions', 'no_symptoms', 'other'].map((type) => (
                  <button
                    key={type}
                    type="button"
                    onClick={() => setSymptomType(type)}
                    className={`px-4 py-2 rounded-lg capitalize ${
                      symptomType === type
                        ? 'bg-primary-600 text-white'
                        : 'bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300'
                    }`}
                  >
                    {type.replace('_', ' ')}
                  </button>
                ))}
              </div>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                Severity
              </label>
              <div className="flex gap-2">
                {(['mild', 'moderate', 'severe'] as const).map((sev) => (
                  <button
                    key={sev}
                    type="button"
                    onClick={() => setSymptomSeverity(sev)}
                    className={`px-4 py-2 rounded-lg capitalize ${
                      symptomSeverity === sev
                        ? 'bg-primary-600 text-white'
                        : 'bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300'
                    }`}
                  >
                    {sev}
                  </button>
                ))}
              </div>
            </div>
          </div>
        );

      case EventType.APPOINTMENT:
        return (
          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                Type
              </label>
              <div className="flex flex-wrap gap-2">
                {['checkup', 'ultrasound', 'blood_test', 'other'].map((type) => (
                  <button
                    key={type}
                    type="button"
                    onClick={() => setAppointmentType(type)}
                    className={`px-4 py-2 rounded-lg capitalize ${
                      appointmentType === type
                        ? 'bg-primary-600 text-white'
                        : 'bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300'
                    }`}
                  >
                    {type.replace('_', ' ')}
                  </button>
                ))}
              </div>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                Provider Name
              </label>
              <input
                type="text"
                value={providerName}
                onChange={(e) => setProviderName(e.target.value)}
                className="input"
                placeholder="Dr. Smith"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                Location
              </label>
              <input
                type="text"
                value={appointmentLocation}
                onChange={(e) => setAppointmentLocation(e.target.value)}
                className="input"
                placeholder="Hospital, London"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                Next Appointment Date
              </label>
              <input
                type="date"
                value={nextAppointmentDate}
                onChange={(e) => setNextAppointmentDate(e.target.value)}
                className="input"
              />
            </div>
          </div>
        );

      case EventType.BLOOD_PRESSURE:
        return (
          <div className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  Systolic (mmHg)
                </label>
                <input
                  type="number"
                  value={systolic}
                  onChange={(e) => setSystolic(e.target.value)}
                  className="input"
                  placeholder="120"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  Diastolic (mmHg)
                </label>
                <input
                  type="number"
                  value={diastolic}
                  onChange={(e) => setDiastolic(e.target.value)}
                  className="input"
                  placeholder="80"
                />
              </div>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                Heart Rate (bpm)
              </label>
              <input
                type="number"
                value={heartRate}
                onChange={(e) => setHeartRate(e.target.value)}
                className="input"
                placeholder="72"
              />
            </div>
          </div>
        );

      case EventType.ULTRASOUND:
        return (
          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                Type
              </label>
              <div className="flex flex-wrap gap-2">
                {['dating', 'anatomy', 'growth', 'other'].map((type) => (
                  <button
                    key={type}
                    type="button"
                    onClick={() => setUltrasoundType(type)}
                    className={`px-4 py-2 rounded-lg capitalize ${
                      ultrasoundType === type
                        ? 'bg-primary-600 text-white'
                        : 'bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300'
                    }`}
                  >
                    {type}
                  </button>
                ))}
              </div>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  Gestational Age (weeks)
                </label>
                <input
                  type="number"
                  step="0.1"
                  value={gestationalAgeWeeks}
                  onChange={(e) => setGestationalAgeWeeks(e.target.value)}
                  className="input"
                  placeholder="12"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  Fetal Heart Rate (bpm)
                </label>
                <input
                  type="number"
                  value={fetalHeartRate}
                  onChange={(e) => setFetalHeartRate(e.target.value)}
                  className="input"
                  placeholder="150"
                />
              </div>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                Findings
              </label>
              <textarea
                value={ultrasoundFindings}
                onChange={(e) => setUltrasoundFindings(e.target.value)}
                className="input min-h-[80px]"
                placeholder="All looking good, measuring on track..."
              />
            </div>
          </div>
        );

      case EventType.KICK_COUNT:
        return (
          <div className="space-y-4">
            <p className="text-sm text-gray-600 dark:text-gray-400 bg-pink-50 dark:bg-pink-900/20 p-3 rounded-lg">
              Count kicks in a session. If you notice reduced fetal movement, contact your GP or midwife immediately.
            </p>
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                Number of Kicks
              </label>
              <input
                type="number"
                min="0"
                value={kickCount}
                onChange={(e) => setKickCount(e.target.value)}
                className="input"
                placeholder="10"
              />
            </div>
            <DurationInput
              value=""
              onChange={() => {}}
              label="Duration (from timer)"
              placeholder="Auto-filled"
            />
            <p className="text-xs text-gray-400">Duration is set automatically when logging from the kick count timer.</p>
          </div>
        );

      case EventType.CONTRACTION:
        return (
          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                Duration (seconds)
              </label>
              <input
                type="number"
                min="0"
                value={contractionDuration}
                onChange={(e) => setContractionDuration(e.target.value)}
                className="input"
                placeholder="45"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                Interval (minutes)
              </label>
              <input
                type="number"
                min="0"
                value={contractionIntervalMinutes}
                onChange={(e) => setContractionIntervalMinutes(e.target.value)}
                className="input"
                placeholder="5"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                Intensity
              </label>
              <div className="flex gap-2">
                {(['mild', 'moderate', 'strong'] as const).map((intensity) => (
                  <button
                    key={intensity}
                    type="button"
                    onClick={() => setContractionIntensity(intensity)}
                    className={`px-4 py-2 rounded-lg capitalize ${
                      contractionIntensity === intensity
                        ? 'bg-primary-600 text-white'
                        : 'bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300'
                    }`}
                  >
                    {intensity}
                  </button>
                ))}
              </div>
            </div>
          </div>
        );

      default:
        return null;
    }
  };

  // Show loading state when fetching event for editing
  if (isLoadingEvent) {
    return (
      <div className="max-w-2xl mx-auto px-4 py-6">
        <div className="flex justify-center py-12">
          <div className="spinner" />
        </div>
      </div>
    );
  }

  return (
    <div className="max-w-2xl mx-auto px-4 py-6">
      {/* Header */}
      <div className="mb-6">
        <button
          onClick={() => navigate(-1)}
          className="text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200 mb-2"
        >
          &larr; Back
        </button>
        <h1 className="text-2xl font-bold text-gray-900 dark:text-white">
          {isEditing ? 'Edit Event' : 'Log Event'}
        </h1>
      </div>

      {error && (
        <div className="mb-6 p-4 bg-red-50 dark:bg-red-900/20 text-red-600 dark:text-red-400 rounded-lg">
          {error}
        </div>
      )}

      <form onSubmit={handleSubmit} className="space-y-6">
        {/* Event type selector (only for new events) */}
        {!selectedType && !isEditing && (
          <div className="card p-4">
            <h2 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">
              What do you want to log?
            </h2>
            <div className="grid grid-cols-3 sm:grid-cols-4 gap-3">
              {EVENT_TYPES.map(({ type, icon, label }) => (
                <button
                  key={type}
                  type="button"
                  onClick={() => setSelectedType(type)}
                  className="flex flex-col items-center p-3 rounded-lg bg-gray-50 dark:bg-gray-800 hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors"
                >
                  <span className="text-2xl">{icon}</span>
                  <span className="mt-1 text-sm text-gray-700 dark:text-gray-300">{label}</span>
                </button>
              ))}
            </div>
          </div>
        )}

        {/* Selected type header */}
        {selectedType && (
          <>
            <div className="flex items-center gap-3 mb-4">
              <span className="text-3xl">
                {EVENT_TYPES.find(t => t.type === selectedType)?.icon}
              </span>
              <div>
                <h2 className="text-lg font-semibold text-gray-900 dark:text-white">
                  {EVENT_TYPES.find(t => t.type === selectedType)?.label}
                </h2>
                {!isEditing && (
                  <button
                    type="button"
                    onClick={() => setSelectedType(null)}
                    className="text-sm text-primary-600 dark:text-primary-400"
                  >
                    Change type
                  </button>
                )}
              </div>
            </div>

            {/* Timestamp */}
            <div className="card p-4">
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                When did this happen?
              </label>
              <input
                type="datetime-local"
                value={timestamp}
                onChange={(e) => setTimestamp(e.target.value)}
                className="input"
              />
            </div>

            {/* Type-specific fields */}
            <div className="card p-4">
              {renderTypeSpecificFields()}
            </div>

            {/* Photos */}
            <div className="card p-4">
              <div className="flex items-center justify-between mb-3">
                <label className="font-medium text-gray-900 dark:text-white">📷 Photos</label>
                <span className="text-xs text-gray-500 dark:text-gray-400">max 2 per log</span>
              </div>

              {(existingPhotos.length > 0 || pendingPhotos.length > 0) && (
                <div className="flex flex-wrap gap-3 mb-3">
                  {existingPhotos.map(photo => (
                    <div key={photo.id} className="relative w-24 h-24 rounded-lg overflow-hidden bg-gray-100 dark:bg-gray-800 flex items-center justify-center">
                      {existingPhotoUrls[photo.id] ? (
                        <img src={existingPhotoUrls[photo.id]} className="w-full h-full object-cover" alt="" />
                      ) : (
                        <span className="text-2xl">🖼️</span>
                      )}
                      <button
                        type="button"
                        onClick={() => handleRemoveExisting(photo.id)}
                        className="absolute top-1 right-1 w-5 h-5 bg-black/60 text-white rounded-full flex items-center justify-center text-xs leading-none"
                      >
                        ✕
                      </button>
                    </div>
                  ))}
                  {pendingPhotos.map((p, i) => (
                    <div key={p.preview} className="relative w-24 h-24 rounded-lg overflow-hidden bg-gray-100 dark:bg-gray-800">
                      <img src={p.preview} className="w-full h-full object-cover opacity-70" alt="" />
                      {p.uploading && (
                        <div className="absolute inset-0 flex items-center justify-center bg-black/20">
                          <span className="spinner" />
                        </div>
                      )}
                      {p.error && (
                        <div className="absolute inset-0 bg-red-500/60 flex items-center justify-center p-1">
                          <span className="text-xs text-white text-center leading-tight">{p.error}</span>
                        </div>
                      )}
                      {!p.uploading && (
                        <button
                          type="button"
                          onClick={() => handleRemovePending(i)}
                          className="absolute top-1 right-1 w-5 h-5 bg-black/60 text-white rounded-full flex items-center justify-center text-xs leading-none"
                        >
                          ✕
                        </button>
                      )}
                    </div>
                  ))}
                </div>
              )}

              {(existingPhotos.length + pendingPhotos.length) < 2 && (
                <label className="btn-secondary cursor-pointer inline-flex items-center gap-2">
                  <span>📷 Add Photo</span>
                  <input
                    type="file"
                    accept="image/*"
                    onChange={handlePhotoSelect}
                    className="hidden"
                  />
                </label>
              )}
            </div>

            {/* Notes */}
            <div className="card p-4">
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                Notes (optional)
              </label>
              <textarea
                value={notes}
                onChange={(e) => setNotes(e.target.value)}
                className="input min-h-[80px]"
                placeholder="Any additional notes..."
              />
            </div>

            {/* Submit */}
            <div className="flex gap-3">
              <button
                type="submit"
                disabled={isSubmitting}
                className="btn-primary flex-1"
              >
                {isSubmitting ? (
                  <>
                    <span className="spinner mr-2" />
                    Saving...
                  </>
                ) : isEditing ? (
                  'Update Event'
                ) : (
                  'Save Event'
                )}
              </button>
              <button
                type="button"
                onClick={() => navigate(-1)}
                className="btn-secondary"
              >
                Cancel
              </button>
            </div>

            {/* Delete button (only when editing) */}
            {isEditing && (
              <button
                type="button"
                onClick={() => setShowDeleteConfirm(true)}
                className="w-full mt-2 py-2 text-red-600 dark:text-red-400 hover:bg-red-50 dark:hover:bg-red-900/20 rounded-lg transition-colors"
              >
                Delete this event
              </button>
            )}
          </>
        )}
      </form>

      {/* Delete confirmation modal */}
      {showDeleteConfirm && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white dark:bg-gray-800 rounded-xl max-w-md w-full p-6">
            <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-2">
              Delete Event?
            </h3>
            <p className="text-gray-600 dark:text-gray-400 mb-6">
              Are you sure you want to delete this event? This action cannot be undone.
            </p>
            <div className="flex gap-3">
              <button
                onClick={handleDelete}
                disabled={isDeleting}
                className="btn-danger flex-1"
              >
                {isDeleting ? 'Deleting...' : 'Yes, Delete'}
              </button>
              <button
                onClick={() => setShowDeleteConfirm(false)}
                disabled={isDeleting}
                className="btn-secondary flex-1"
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Upright timer prompt modal */}
      {showUprightPrompt && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white dark:bg-gray-800 rounded-xl max-w-md w-full p-6">
            <div className="text-center mb-4">
              <span className="text-4xl">⏳</span>
            </div>
            <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-2 text-center">
              Start upright timer?
            </h3>
            <p className="text-gray-600 dark:text-gray-400 mb-6 text-center">
              Keep {babyName || 'baby'} upright for {uprightDuration} minutes to help prevent spit-up.
            </p>
            <div className="flex gap-3">
              <button
                onClick={async () => {
                  await startUprightTimer(babyId!, uprightDuration);
                  navigate(`/app/dashboard`);
                }}
                className="flex-1 bg-amber-500 hover:bg-amber-600 text-white py-3 px-4 rounded-lg font-semibold transition-colors"
              >
                Start Timer
              </button>
              <button
                onClick={() => navigate(`/app/baby/${babyId}/history`)}
                className="btn-secondary flex-1"
              >
                Skip
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Exit without saving confirmation modal */}
      {showExitConfirm && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white dark:bg-gray-800 rounded-xl max-w-md w-full p-6">
            <div className="text-center mb-4">
              <span className="text-4xl">⚠️</span>
            </div>
            <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-2 text-center">
              Leave without saving?
            </h3>
            <p className="text-gray-600 dark:text-gray-400 mb-6 text-center">
              You have unsaved changes. If you leave now, all entered information will be lost.
            </p>
            <div className="flex gap-3">
              <button
                onClick={() => {
                  setShowExitConfirm(false);
                  blocker.proceed?.();
                }}
                className="btn-danger flex-1"
              >
                Leave
              </button>
              <button
                onClick={() => {
                  setShowExitConfirm(false);
                  blocker.reset?.();
                }}
                className="btn-primary flex-1"
              >
                Stay on Page
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
